import { Button } from '@aws-amplify/ui-react';
import type { Edulead } from 'edulead';
import { useState } from 'react';
import { TbPencil, TbPlus } from 'react-icons/tb';
import { toast } from 'react-toastify';
import ConfirmModal from '../../custom-components/ConfirmModal';
import LoadingBars from '../../custom-components/LoadingBars';
import { RemoveButton } from '../../custom-components/RemoveButton';
import UniversalTag from '../../custom-components/UniversalTag';
import { usePermission } from '../../hooks/usePermission';
import { toastV4Error } from '../../services/edulead-v4';
import { eduleadV4 } from '../../services/edulead-v4-client';
import { beaconColorCss } from './beacon-colors';
import { PanelInfo } from './PanelInfo';

/** What a beacon is and how it is used, behind the list's info icon. */
const BEACONS_INFO =
  "A beacon is a named list of districts, such as your customers or a campaign's targets. " +
  'Select one to see it on the map, then click districts or draw with the brush, rectangle or ' +
  'circle to change it; Save keeps the changes. In Search, the Expansion filter finds people ' +
  "within a radius of a beacon's districts.";

/** The name a new beacon gets; the form renames it. */
const NEW_BEACON_NAME = 'Untitled beacon';

/**
 * Whether a click adds to the selection: Cmd on a Mac (where Ctrl+click is a right-click), Ctrl
 * elsewhere, as the OS's own lists.
 */
const IS_MAC = /Mac|iPhone|iPad/.test(navigator.userAgent);
export const isAdditiveClick = (e: React.MouseEvent) => (IS_MAC ? e.metaKey : e.ctrlKey);

interface BeaconListProps {
  beacons: Edulead.EduleadV4Beacon[];
  isLoading: boolean;
  onOpen: (id: string) => void;
  /**
   * With it a click on a row selects the beacon (shown on the map), a Cmd/Ctrl+click adds it to
   * the selection or takes it out (isAdditive), and an edit button opens it; without, a click
   * opens it.
   */
  onSelect?: (id: string, isAdditive: boolean) => void;
  /** The selected beacons' ids, marked in the list. */
  selectedIds?: string[];
  onCreated: (beacon: Edulead.EduleadV4Beacon) => void;
  onDeleted: (id: string) => void;
}

/**
 * The team's beacons by name: select one to see it on the map, open one to edit it on the map,
 * create an empty one, or delete one.
 */
export function BeaconList({
  beacons,
  isLoading,
  onOpen,
  onSelect,
  selectedIds = [],
  onCreated,
  onDeleted,
}: BeaconListProps) {
  const { hasPermission } = usePermission();
  const canManage = hasPermission('beacons:manage');
  const [isCreating, setIsCreating] = useState(false);
  // The beacon whose delete is being confirmed, and whether the delete is under way.
  const [toDelete, setToDelete] = useState<Edulead.EduleadV4Beacon | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  /** Creates an empty beacon and opens it, so its districts can be clicked on the map. */
  const handleCreate = async () => {
    try {
      setIsCreating(true);
      const { beacon } = await eduleadV4.beacons.create({
        request_id: crypto.randomUUID(),
        body: { name: NEW_BEACON_NAME },
      });
      if (beacon?.id) {
        onCreated(beacon);
        onOpen(beacon.id);
      }
    } catch (error) {
      toastV4Error(error, 'Failed to create beacon');
    } finally {
      setIsCreating(false);
    }
  };

  /** Deletes the beacon the dialog confirmed. */
  const confirmDelete = async () => {
    if (!toDelete?.id) return;
    try {
      setIsDeleting(true);
      await eduleadV4.beacons.delete({ id: toDelete.id });
      toast.success('Beacon deleted');
      onDeleted(toDelete.id);
      setToDelete(null);
    } catch (error) {
      toastV4Error(error, 'Failed to delete beacon');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="beacon-list">
      <div className="navigator-beacons-panel__bar beacon-list__header">
        <span>Beacons</span>
        <span className="navigator-beacons-panel__bar-actions">
          <PanelInfo text={BEACONS_INFO} />
          {canManage && (
            <Button
              size="small"
              className="nohover tiny"
              aria-label="New beacon"
              isDisabled={isCreating}
              onClick={handleCreate}
            >
              <TbPlus />
            </Button>
          )}
        </span>
      </div>
      {isLoading ? (
        <LoadingBars numberOfRows={4} />
      ) : beacons.length === 0 ? (
        <div className="beacon-list__empty">
          A beacon is a list of districts.
          {canManage ? ' Create one and click districts on the map.' : ''}
        </div>
      ) : (
        beacons.map((beacon) => (
          <div
            key={beacon.id}
            className={`beacon-list__item ${beacon.id && selectedIds.includes(beacon.id) ? 'beacon-list__item--selected' : ''}`}
            onClick={(e) => {
              if (!beacon.id) return;
              if (onSelect) onSelect(beacon.id, isAdditiveClick(e));
              else onOpen(beacon.id);
            }}
          >
            <div className="beacon-list__item-text">
              <span
                className="beacon-list__swatch"
                style={{ backgroundColor: beaconColorCss(beacon.color) }}
              />
              <div className="beacon-list__name">{beacon.name}</div>
              <UniversalTag
                category={(beacon.institution_count ?? 0).toLocaleString()}
                classOverride="tag-neutral"
              />
            </div>
            {(onSelect || canManage) && (
              // A click on a button edits or deletes; it must not also select the beacon.
              <div className="beacon-list__actions" onClick={(e) => e.stopPropagation()}>
                {onSelect && (
                  <Button
                    className="nohover tiny beacon-list__edit"
                    aria-label={canManage ? 'Edit beacon' : 'Open beacon'}
                    onClick={() => beacon.id && onOpen(beacon.id)}
                  >
                    <TbPencil />
                  </Button>
                )}
                {canManage && (
                  <RemoveButton
                    title="Delete beacon"
                    isTitleHidden
                    className="nohover"
                    onClick={() => setToDelete(beacon)}
                  />
                )}
              </div>
            )}
          </div>
        ))
      )}
      <ConfirmModal
        isOpen={toDelete !== null}
        onClose={() => !isDeleting && setToDelete(null)}
        onConfirm={confirmDelete}
        title="Delete beacon?"
        message={`"${toDelete?.name ?? ''}" and its institutions are deleted. This cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        isLoading={isDeleting}
        loadingText="Deleting..."
      />
    </div>
  );
}
